<template>
  <teleport to="body">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
      <div class="w-full max-w-4xl bg-gray-800 text-indigo-200 rounded-md shadow-2xl border border-gray-700 overflow-hidden animate-scaleIn flex flex-col" style="max-height:60vh;">
        <!-- Sticky Header -->
        <header class="bg-gray-800 border-b border-gray-700">
          <div class="flex items-center justify-between p-4">
            <div class="flex items-center gap-3">
              <i class="fa-solid fa-tower-observation text-indigo-300 text-xl"></i>
              <div>
                <h2 class="text-base sm:text-lg font-semibold text-indigo-300">{{ outpost?.name || outpostId }}</h2>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <button
                @click="emitClose"
                class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group focus:outline-none p-2"
                aria-label="ปิด"
              >
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
          </div>

          <!-- Primary Tabs -->
          <nav v-if="!isSelectingVillagers" class="grid grid-cols-3 bg-gray-700 bg-opacity-60">
            <button
              v-for="tab in filteredTabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              class="relative px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 border-b-2"
              :class="activeTab === tab.id ? 'text-indigo-300 border-indigo-600' : 'text-gray-300 border-transparent hover:bg-gray-700 hover:text-indigo-200'"
            >
              <i :class="tab.icon"></i>
              <span>{{ tab.id === 'defenders' ? defendersTabName : tab.name }}</span>
              <span
                v-if="activeTab === tab.id"
                class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"
              ></span>
            </button>
          </nav>
        </header>

        <!-- Content -->
        <main class="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin" id="style-1">
          <template v-if="!isSelectingVillagers">
            <!-- BASIC -->
            <section v-if="activeTab === 'basic'" class="space-y-4 animate-fadeIn">
              <!-- Image -->
              <div class="flex items-center justify-center">
                <img
                  v-if="outpost?.imageUrl && !imgBroken"
                  :src="outpost.imageUrl"
                  class="w-20 h-20 object-cover rounded-full"
                  @error="imgBroken = true"
                />
                <div v-else class="text-sm text-gray-400">ไม่มีรูปภาพ</div>
              </div>

              <!-- Quick facts -->
              <div class="rounded-lg border border-gray-700 bg-gray-800 bg-opacity-70 px-3 py-3 sm:px-4 sm:py-4">
                <!-- เจ้าของ + ตำแหน่ง -->
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                    <div class="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-500 bg-opacity-20 border border-indigo-500 border-opacity-30">
                      <i class="fa-solid fa-user text-indigo-300"></i>
                    </div>

                    <div>
                      <div class="text-sm text-gray-300 leading-tight">{{ characterName || 'NPC' }}</div>
                      <div class="text-xs text-gray-400 leading-tight">{{ outpost?.location || '-' }}</div>
                    </div>

                    <img
                      v-if="characterImageUrl"
                      :src="characterImageUrl"
                      class="w-7 h-7 rounded-full object-cover border border-indigo-500 ml-1"
                      alt="รูปตัวละคร"
                    />
                  </div>

                  <div class="inline-flex items-center space-x-1 px-2 py-1 rounded-md bg-indigo-600 bg-opacity-20 border border-indigo-500 border-opacity-40 text-indigo-200 text-xs font-semibold">
                    <i class="fa-solid fa-gauge-high"></i>
                    <span>LV {{ outpost?.level ?? 0 }}</span>
                  </div>
                </div>

                <!-- ตารางข้อมูล -->
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs text-gray-300">
                  <div class="rounded-md border border-gray-700 bg-gray-800 bg-opacity-60 px-3 py-2 hover:bg-gray-700 transition-colors">
                    <div class="flex items-center space-x-1 text-gray-400 text-xxs">
                      <i class="fa-solid fa-gem text-indigo-300"></i>
                      <span>เพชร/วัน</span>
                    </div>
                    <div class="mt-0.5 text-sm font-semibold text-gray-200">
                      {{ outpost?.dailyDiamondReward ?? 0 }}
                    </div>
                  </div>

                  <div class="rounded-md border border-gray-700 bg-gray-800 bg-opacity-60 px-3 py-2 hover:bg-gray-700 transition-colors">
                    <div class="flex items-center space-x-1 text-gray-400 text-xxs">
                      <i class="fa-solid fa-trophy text-indigo-300"></i>
                      <span>คะแนนยึดครอง</span>
                    </div>
                    <div class="mt-0.5 text-sm font-semibold text-gray-200">
                      {{ outpost?.occupationScore ?? 0 }}
                    </div>
                  </div>

                  <div class="rounded-md border border-gray-700 bg-gray-800 bg-opacity-60 px-3 py-2 hover:bg-gray-700 transition-colors">
                    <div class="flex items-center space-x-1 text-gray-400 text-xxs">
                      <i class="fa-solid fa-shield-halved text-indigo-300"></i>
                      <span>ป้องกันสูงสุด</span>
                    </div>
                    <div class="mt-0.5 text-sm font-semibold text-gray-200">
                      {{ outpost?.maxDefenderVillagers ?? 0 }} คน
                    </div>
                  </div>

                  <div class="rounded-md border border-gray-700 bg-gray-800 bg-opacity-60 px-3 py-2 hover:bg-gray-700 transition-colors">
                    <div class="flex items-center space-x-1 text-gray-400 text-xxs">
                      <i class="fa-solid fa-users-rays text-indigo-300"></i>
                      <span>บุกสูงสุด</span>
                    </div>
                    <div class="mt-0.5 text-sm font-semibold text-gray-200">
                      {{ outpost?.maxAttackerVillagers ?? 0 }} คน
                    </div>
                  </div>

                  <div
                    v-if="remainingOutpostMs > 0"
                    class="col-span-2 sm:col-span-1 rounded-md border border-gray-700 bg-gray-800 bg-opacity-60 px-3 py-2 hover:bg-gray-700 transition-colors"
                  >
                    <div class="flex items-center space-x-1 text-gray-400 text-xxs">
                      <i class="fa-solid fa-hourglass-half text-indigo-300"></i>
                      <span>ยึดได้อีก</span>
                    </div>
                    <div class="mt-0.5 text-sm font-semibold text-gray-200">
                      {{ formatRemaining(remainingOutpostMs) }}
                    </div>
                  </div>
                </div>

                <!-- แถบสถานะ -->
                <div class="mt-3 flex flex-wrap items-center space-x-2 text-xxs">
                  <span
                    class="inline-flex items-center space-x-1 px-2 py-1 rounded-md border"
                    :class="isOwnOutpost ? 'border-green-600 bg-green-600 bg-opacity-10 text-green-300' : 'border-indigo-600 bg-indigo-600 bg-opacity-10 text-indigo-300'"
                  >
                    <i class="fa-solid" :class="isOwnOutpost ? 'fa-circle-check' : 'fa-person-rifle'"></i>
                    <span>{{ isOwnOutpost ? 'เป็นของคุณ' : 'NPC / ผู้อื่น' }}</span>
                  </span>

                  <span
                    v-if="treasureOpened"
                    class="inline-flex items-center space-x-1 px-2 py-1 rounded-md border border-yellow-600 bg-yellow-600 bg-opacity-10 text-yellow-300"
                  >
                    <i class="fa-solid fa-treasure-chest"></i>
                    <span>เปิดแล้ววันนี้ • {{ countdownText }}</span>
                  </span>

                  <span
                    v-else-if="isTreasureAvailable"
                    class="inline-flex items-center space-x-1 px-2 py-1 rounded-md border border-yellow-500 bg-yellow-500 bg-opacity-10 text-yellow-200"
                  >
                    <i class="fa-solid fa-treasure-chest"></i>
                    <span>กล่องพร้อมเปิด</span>
                  </span>

                  <span
                    v-else
                    class="inline-flex items-center space-x-1 px-2 py-1 rounded-md border border-gray-600 bg-gray-700 bg-opacity-30 text-gray-300"
                  >
                    <i class="fa-regular fa-clock"></i>
                    <span>คูลดาวน์ {{ formatRemaining(remainingTreasureMs) }}</span>
                  </span>
                </div>
              </div>

              <!-- Clear Cooldown Banner -->
              <div
                v-if="isClearCooldown"
                class="rounded-lg border border-gray-600 bg-gray-900 bg-opacity-90 p-5 flex flex-col items-center justify-center gap-3 text-center"
              >
                <div class="flex items-center gap-2 text-gray-400">
                  <i class="fa-solid fa-shield-halved text-2xl"></i>
                  <span class="font-semibold text-sm text-gray-300">Outpost กำลังฟื้นตัว</span>
                </div>

                <p class="text-xs text-gray-500">ไม่สามารถยึดหรือเคลียร์ได้จนกว่าจะครบกำหนด</p>

                <div class="px-5 py-3 bg-gray-800 border border-gray-600 rounded-lg">
                  <p class="text-xxs text-gray-400 mb-1">เปิดให้ยึดได้อีก</p>
                  <p class="text-xl font-mono font-bold text-indigo-300 tabular-nums tracking-wider">
                    {{ formatRemainingLong(remainingClearedMs) }}
                  </p>
                </div>

                <p class="text-xxs text-gray-600">Outpost จะกลับมาให้ยึดได้โดยอัตโนมัติหลังหมดเวลา</p>
              </div>

              <!-- Description -->
              <div class="bg-gray-800 bg-opacity-70 border border-gray-700 rounded-md p-3">
                <div class="text-xs text-gray-400 mb-1">คำอธิบาย</div>
                <div class="text-sm whitespace-pre-wrap">{{ outpost?.description || '—' }}</div>
              </div>

              <!-- Treasure Box Section -->
              <div v-if="isOwnOutpost" class="relative">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div class="flex items-center gap-2">
                    <button
                      v-if="!isOpeningTreasure"
                      @click="confirmOpen = true"
                      :disabled="!isTreasureAvailable"
                      class="group inline-flex items-center gap-3 px-4 py-2 rounded-md transition-colors duration-200 focus:outline-none border"
                      :class="[
                        !isTreasureAvailable
                          ? 'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed'
                          : 'bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-500',
                      ]"
                      aria-label="เปิดกล่องสมบัติ"
                      @mouseenter="showHintOpen = true"
                      @mouseleave="showHintOpen = false"
                    >
                      <span class="relative inline-flex">
                        <i
                          class="fas fa-treasure-chest text-lg"
                          :class="!isTreasureAvailable ? 'text-yellow-300 opacity-60' : 'text-yellow-300'"
                        ></i>
                      </span>

                      <div class="flex flex-col items-start leading-tight">
                        <span class="font-semibold text-sm">เปิดกล่องสมบัติ</span>
                        <span class="text-xxs opacity-80" v-if="isTreasureAvailable">รับรางวัลยึดครองประจำวัน</span>
                        <span class="text-xxs opacity-80" v-else-if="treasureOpened">ถูกเปิดแล้ววันนี้</span>
                        <span class="text-xxs opacity-80" v-else-if="remainingTreasureMs > 0">คูลดาวน์เหลือ {{ formatRemaining(remainingTreasureMs) }}</span>
                      </div>
                    </button>

                    <div
                      v-else
                      class="inline-flex items-center gap-3 px-4 py-2 rounded-md bg-indigo-600 text-white border border-indigo-500"
                    >
                      <i class="fas fa-treasure-chest animate-shake text-yellow-300 text-xl"></i>
                      <span class="animate-pulse text-sm">กำลังเปิดกล่องสมบัติ...</span>
                    </div>

                    <div
                      v-if="showHintOpen && isTreasureAvailable && !isOpeningTreasure"
                      class="hidden sm:block text-xxs bg-gray-900 border border-gray-700 px-2 py-1 rounded-md"
                    >
                      เปิดเพื่อรับเพชรและสุ่มไอเท็ม
                    </div>
                  </div>

                  <div class="text-xxs sm:text-xs text-indigo-300 space-y-1">
                    <p v-if="treasureOpened">{{ countdownText }}</p>
                    <p v-else-if="remainingTreasureMs > 0">คูลดาวน์เหลือ {{ formatRemaining(remainingTreasureMs) }}</p>
                    <p v-if="treasureMessage" :class="treasureMessage.includes('สำเร็จ') ? 'text-green-400' : 'text-red-400'">
                      {{ treasureMessage }}
                    </p>
                  </div>
                </div>

                <transition name="fade">
                  <div
                    v-if="confirmOpen && isTreasureAvailable"
                    class="mt-2 p-3 rounded-md border bg-gray-900 border-gray-700"
                  >
                    <div class="flex items-start gap-3">
                      <i class="fas fa-exclamation-triangle text-yellow-400 mt-0.5"></i>
                      <div class="flex-1">
                        <p class="text-sm text-indigo-200 font-medium">ยืนยันการเปิดกล่องสมบัติ</p>
                        <p class="text-xxs text-indigo-300">สามารถเปิดได้วันละครั้ง รีเซ็ตเวลา 21:00 น. (เวลาไทย)</p>
                      </div>
                    </div>

                    <div class="mt-3 flex items-center gap-2">
                      <button
                        @click="openTreasureBox"
                        class="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-xs border border-indigo-500"
                      >
                        ยืนยันเปิด
                      </button>
                      <button
                        @click="confirmOpen = false"
                        class="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-indigo-200 text-xs border border-gray-600"
                      >
                        ยกเลิก
                      </button>
                    </div>
                  </div>
                </transition>
              </div>
            </section>

            <!-- LOOT -->
            <section v-if="activeTab === 'loot'" class="space-y-4 animate-fadeIn">
              <div class="bg-gray-900 bg-opacity-60 border border-gray-700 rounded-md p-3">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="text-xs font-semibold text-indigo-300">สมบัติที่ดรอปได้</h3>
                  <div class="text-xxs text-gray-400">{{ (outpost?.lootItems || []).length }} ชิ้น</div>
                </div>

                <div v-if="outpost?.lootItems?.length" class="grid grid-cols-5 sm:grid-cols-10 gap-2">
                  <button
                    v-for="(loot, i) in sortedLootItems"
                    :key="i"
                    class="group relative cursor-pointer rounded-md overflow-hidden border border-gray-700 hover:border-indigo-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    @click="showItemDetails(loot.item)"
                  >
                    <img
                      v-if="itemDetails[loot.item]?.img"
                      :src="itemDetails[loot.item].img"
                      :alt="loot.item"
                      loading="lazy"
                      class="w-full h-auto object-cover"
                      @error="handleImageError"
                    />

                    <div v-if="isLoadingItem[loot.item]" class="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-60">
                      <div class="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                    </div>

                    <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 text-left">
                      <div class="text-xxs font-semibold text-indigo-200 truncate">{{ loot.item }}</div>
                      <div class="text-xxs text-gray-300">~{{ toPercent(loot.chance) }}%</div>
                    </div>
                  </button>
                </div>

                <p v-else class="text-indigo-300 text-xs text-center py-6">—</p>
              </div>
            </section>

            <!-- DEFENDERS -->
            <section v-if="activeTab === 'defenders'" class="space-y-4 animate-fadeIn">
              <div class="bg-gray-900 bg-opacity-60 border border-gray-700 rounded-md p-3">
                <div class="flex items-center justify-between mb-2">
                  <h3 class="text-xs font-semibold text-indigo-300">{{ defendersTabName }}</h3>

                  <div class="flex items-center gap-2">
                    <div class="text-xxs text-gray-400">{{ validDefenders.length }} ตัว</div>

                    <button
                      v-if="isAdmin && (outpostPrivate?.defenderVillagerIds || []).length"
                      @click="migrateOutpostDefenders"
                      class="px-2 py-1 bg-yellow-700 hover:bg-yellow-600 text-white text-xxs rounded border border-yellow-500 flex items-center gap-1"
                    >
                      <i class="fa-solid fa-arrows-rotate"></i>
                      Migrate V2
                    </button>
                  </div>
                </div>

                <!-- Invalid ID เฉพาะ schema เก่าเท่านั้น: ตอนนี้ script ใหม่ return [] แล้ว ส่วนนี้จะไม่แสดง -->
                <div v-if="isAdmin && invalidDefenderIds.length" class="mb-4 pb-4 border-b border-gray-700">
                  <div class="flex items-center justify-between mb-2">
                    <p class="text-xs font-semibold text-red-400 flex items-center gap-1">
                      <i class="fa-solid fa-triangle-exclamation"></i>
                      ชาวบ้านที่ถูกลบ/หายไป ({{ invalidDefenderIds.length }} รายการ)
                    </p>

                    <button
                      @click="removeAllInvalidDefenders"
                      class="px-2 py-1 bg-red-800 hover:bg-red-700 text-white text-xxs rounded border border-red-600 flex items-center gap-1"
                    >
                      <i class="fa-solid fa-trash-can"></i>
                      ลบทั้งหมด
                    </button>
                  </div>

                  <div class="space-y-2">
                    <div
                      v-for="id in invalidDefenderIds"
                      :key="id"
                      class="flex items-center justify-between bg-red-900 bg-opacity-30 border border-red-800 rounded-md p-2 text-xs"
                    >
                      <div class="flex items-center gap-2 min-w-0">
                        <span class="text-red-300 font-mono truncate">{{ id }}</span>

                        <button
                          @click="copyToClipboard(id)"
                          class="flex-shrink-0 px-1.5 py-0.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xxs rounded border border-gray-600 flex items-center gap-1"
                          title="คัดลอก ID"
                        >
                          <i class="fa-regular fa-copy"></i>
                          คัดลอก
                        </button>
                      </div>

                      <button
                        @click="removeInvalidDefender(id)"
                        class="flex-shrink-0 ml-2 px-2 py-1 bg-red-700 hover:bg-red-600 text-white text-xxs rounded border border-red-600"
                      >
                        ลบออก
                      </button>
                    </div>
                  </div>
                </div>

                <!-- ชาวบ้านจริง / NPC ป้องกัน -->
                <div v-if="validDefenders.length" class="grid grid-cols-1 gap-4">
                  <div
                    v-for="def in validDefenders"
                    :key="def.id"
                    class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition"
                  >
                    <div class="p-3 flex items-center">
                      <div class="relative">
                        <img
                          :src="def.img || def.template?.normalFace || def.normalFace || 'https://via.placeholder.com/64?text=No+Img'"
                          :alt="def.name"
                          class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                          @error="handleImageError"
                        />

                        <div
                          class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                        >
                          {{ def.isNpcDefender ? '??' : (def.level ?? def.template?.level ?? 1) }}
                        </div>
                      </div>

                      <div class="ml-3 flex-1">
                        <div class="flex items-center justify-between">
                          <div class="font-semibold text-gray-100 truncate">
                            {{ def.name }}

                            <button
                              @click="openPowerModal(def)"
                              class="ml-1 text-indigo-300 hover:text-indigo-100"
                            >
                              <i class="fa-solid fa-circle-info"></i>
                            </button>
                          </div>
                        </div>

                        <div class="flex items-center space-x-1 px-1">
                          <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                            <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                            <span class="font-mono">{{ getDice(def, 'defenseDice') || 6 }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="px-3 pb-3 space-y-2">
                      <div class="grid grid-cols-3 gap-1">
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          sta: {{ getTotalStat(def, 'stamina') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          str: {{ getTotalStat(def, 'str') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          agi: {{ getTotalStat(def, 'agi') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          int: {{ getTotalStat(def, 'int') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          luck: {{ getTotalStat(def, 'luck') }}
                        </span>
                        <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                          def: {{ getTotalStat(def, 'defense') }}
                        </span>
                      </div>
                    </div>

                    <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
                  </div>
                </div>

                <p
                  v-if="!validDefenders.length && !invalidDefenderIds.length"
                  class="text-indigo-300 text-xs text-center py-6"
                >
                  —
                </p>
              </div>
            </section>
          </template>

          <template v-else>
            <!-- Villager Selection -->
            <section class="space-y-4 animate-fadeIn">
              <h3 class="text-sm font-semibold text-indigo-300">
                เลือกชาวบ้านสำหรับ{{ attackMode === 'seize' ? 'บุกยึด' : 'เคลียร์ผู้ยึดครอง' }} (สูงสุด {{ maxAttackers }} คน) - เลือกแล้ว {{ selectedVillagerIds.length }} คน
              </h3>

              <!-- Mode Selection -->
              <div class="flex gap-2 mb-4">
                <button
                  @click="attackMode = 'seize'"
                  class="px-3 py-1.5 rounded-md text-xs border"
                  :class="{
                    'bg-indigo-600 text-white border-indigo-500': attackMode === 'seize' && !hasMaxOutposts,
                    'bg-gray-700 text-indigo-200 border-gray-600 hover:bg-gray-600': attackMode !== 'seize' && !hasMaxOutposts,
                    'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed': hasMaxOutposts
                  }"
                  :disabled="hasMaxOutposts"
                >
                  บุกยึด
                </button>

                <button
                  @click="attackMode = 'clear'"
                  class="px-3 py-1.5 rounded-md text-xs border"
                  :class="{
                    'bg-indigo-600 text-white border-indigo-500': attackMode === 'clear' && !cannotClear,
                    'bg-gray-700 text-indigo-200 border-gray-600 hover:bg-gray-600': attackMode !== 'clear' && !cannotClear,
                    'bg-gray-700 border-gray-600 text-gray-300 cursor-not-allowed': cannotClear
                  }"
                  :disabled="cannotClear"
                >
                  เคลียร์ผู้ยึดครอง
                </button>
              </div>

              <!-- Anonymous toggle -->
              <transition name="fade">
                <div
                  v-if="attackMode === 'clear'"
                  class="mb-4 rounded-lg border p-3 transition-colors duration-200"
                  :class="isAnonymous
                    ? 'bg-gray-900 border-gray-600'
                    : 'bg-indigo-900 bg-opacity-20 border-indigo-700'"
                >
                  <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-2">
                      <i
                        class="fa-solid text-base transition-colors duration-200"
                        :class="isAnonymous ? 'fa-user-secret text-gray-400' : 'fa-user text-indigo-300'"
                      ></i>

                      <div>
                        <p class="text-xs font-semibold" :class="isAnonymous ? 'text-gray-300' : 'text-indigo-200'">
                          {{ isAnonymous ? 'ปิดบังตัวตน' : 'เปิดเผยตัวตน' }}
                        </p>
                        <p class="text-xxs mt-0.5" :class="isAnonymous ? 'text-gray-500' : 'text-indigo-400'">
                          {{ isAnonymous
                            ? 'เจ้าของและผู้เล่นคนอื่นจะไม่รู้ว่าใครมาเคลียร์'
                            : 'เจ้าของจะได้รับแจ้งว่าคุณมาเคลียร์' }}
                        </p>
                      </div>
                    </div>

                    <button
                      @click="isAnonymous = !isAnonymous"
                      class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none flex-shrink-0"
                      :class="isAnonymous ? 'bg-gray-600' : 'bg-indigo-600'"
                      :aria-label="isAnonymous ? 'เปิดเผยตัวตน' : 'ปิดบังตัวตน'"
                    >
                      <span
                        class="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300"
                        :class="isAnonymous ? 'translate-x-6' : 'translate-x-1'"
                      ></span>
                    </button>
                  </div>
                </div>
              </transition>

              <!-- Reward Info for Clear Mode -->
              <div class="bg-gray-800 bg-opacity-70 border border-gray-700 rounded-md p-3 text-sm text-indigo-300">
                <p class="font-semibold mb-2">รางวัลเมื่อเคลียร์ผู้ยึดครองสำเร็จ:</p>
                <ul class="list-disc pl-5 space-y-1">
                  <li>เพชร: {{ outpost?.dailyDiamondReward ?? 0 }} เพชร</li>
                  <li>คะแนนยึดครอง: {{ outpost?.occupationScore ?? 0 }} คะแนน</li>
                </ul>
                <p class="mt-2 text-xs text-gray-400">
                  หมายเหตุ: ถ้าเคลียร์ผู้ยึดครองสำเร็จ ผู้เล่นจะได้รับคะแนนและเพชรตามที่กำหนด และ outpost จะกลับสู่การควบคุมของ NPC และ<strong class="text-yellow-400">ล็อคไม่ให้ยึดได้ 3 วัน</strong>
                </p>
              </div>

              <div v-if="playerVillagers.length > 0" class="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div
                  v-for="v in sortedPlayerVillagers"
                  :key="v.id"
                  class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition"
                >
                  <div class="p-3 flex items-center">
                    <div class="relative">
                      <img
                        :src="v.img || v.template?.normalFace || 'https://via.placeholder.com/64?text=No+Img'"
                        :alt="v.name"
                        class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                        @error="handleImageError"
                      />

                      <div
                        v-if="v.level || v.template?.level"
                        class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                      >
                        {{ v.level ?? v.template?.level }}
                      </div>
                    </div>

                    <div class="ml-3 flex-1">
                      <div class="flex items-center justify-between">
                        <div class="font-semibold text-gray-100 truncate">
                          {{ v.name }}

                          <button @click="openPowerModal(v)" class="ml-1 text-indigo-300 hover:text-indigo-100">
                            <i class="fa-solid fa-circle-info"></i>
                          </button>
                        </div>
                      </div>

                      <div class="flex items-center space-x-1 px-1">
                        <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                          <i class="fa-solid fa-dice text-red-300 text-xs"></i>
                          <span class="font-mono">6</span>
                        </div>
                        <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                          <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                          <span class="font-mono">6</span>
                        </div>
                      </div>

                      <div class="text-xs text-gray-400 truncate" v-if="v.template?.note">
                        {{ v.template.note }}
                      </div>
                    </div>
                  </div>

                  <div class="px-3 pb-3 space-y-2">
                    <div class="grid grid-cols-3 gap-1">
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        sta: {{ getTotalStat(v, 'stamina') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        str: {{ getTotalStat(v, 'str') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        agi: {{ getTotalStat(v, 'agi') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        int: {{ getTotalStat(v, 'int') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        luck: {{ getTotalStat(v, 'luck') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        def: {{ getTotalStat(v, 'defense') }}
                      </span>
                    </div>
                  </div>

                  <div class="px-3 pb-3">
                    <button
                      @click="toggleSelect(v.id)"
                      class="w-full px-2 py-1 text-white rounded text-xs transition"
                      :class="[
                        selectedVillagerIds.includes(v.id) ? 'bg-green-700 hover:bg-green-600' :
                        isSelectable(v) ? 'bg-indigo-700 hover:bg-indigo-600' : 'bg-gray-700 cursor-not-allowed'
                      ]"
                      :disabled="!isSelectable(v)"
                    >
                      {{ selectedVillagerIds.includes(v.id) ? 'ยกเลิก' : (isSelectable(v) ? 'เลือก' : 'เลือกไม่ได้') }}
                    </button>
                  </div>

                  <div v-if="remainingMs(v) > 0" class="px-3 pb-3 text-xxs text-red-400">
                    สามารถส่งไปบุกได้หลัง {{ formatRemaining(remainingMs(v)) }}
                  </div>

                  <div v-if="!v.canAssign" class="px-3 pb-3 text-xxs text-red-400">
                    ชาวบ้านไม่คุ้นเคยกับสถานที่นี้
                  </div>

                  <div v-if="isNameAlreadySelected(v.name) && !selectedVillagerIds.includes(v.id)" class="px-3 pb-3 text-xxs text-red-400">
                    ไม่สามารถเลือกชาวบ้านชื่อซ้ำกันได้
                  </div>

                  <div v-if="selectedVillagerIds.length >= maxAttackers && !selectedVillagerIds.includes(v.id)" class="px-3 pb-3 text-xxs text-red-400">
                    ถึงขีดจำกัดจำนวนชาวบ้านแล้ว
                  </div>

                  <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
                </div>
              </div>

              <p v-else class="text-indigo-300 text-xs text-center py-6">คุณไม่มีชาวบ้านสำหรับบุกยึด</p>
            </section>
          </template>
        </main>

        <!-- Footer -->
        <footer class="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-3 flex items-center justify-end gap-2">
          <template v-if="!isSelectingVillagers">
            <div
              v-if="attackDisabled && !isOwnOutpost"
              class="mr-auto flex items-center space-x-2 text-red-400 text-xs font-medium bg-red-900 bg-opacity-20 border border-red-700 rounded-md px-3 py-1.5"
            >
              <i class="fa-solid fa-exclamation-triangle"></i>
              <span>{{ disableReason }}</span>
            </div>

            <button
              v-if="!isOwnOutpost"
              :disabled="attackDisabled"
              class="text-xs px-3 py-2 rounded-md transition flex items-center justify-center"
              :class="attackDisabled ? 'bg-gray-700 border border-gray-600 text-gray-300 cursor-not-allowed' : 'bg-indigo-700 border border-indigo-600 hover:bg-indigo-600 text-white'"
              @click="startAttack"
            >
              <span v-if="!attackStarting">{{ hasMaxOutposts ? 'ส่งชาวบ้านไปเคลียร์' : 'ส่งชาวบ้านไปยึด' }}</span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>

            <button
              class="text-xs px-3 py-2 rounded-md bg-gray-700 border border-gray-600 hover:bg-gray-600 transition"
              @click="emitClose"
            >
              ปิด
            </button>
          </template>

          <template v-else>
            <button
              class="text-xs px-3 py-2 rounded-md bg-gray-700 border border-gray-600 hover:bg-gray-600 transition"
              @click="cancelSeize"
            >
              ยกเลิก
            </button>

            <button
              class="text-xs px-3 py-2 rounded-md transition flex items-center justify-center"
              :class="[
                (mustPickMax && !isPartyFull) || attacking
                  ? 'bg-gray-700 border border-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-indigo-700 border border-indigo-600 hover:bg-indigo-600 text-white'
              ]"
              :disabled="(mustPickMax && !isPartyFull) || attacking"
              @click="attackOutpost"
            >
              <span v-if="!attacking">
                <template v-if="mustPickMax && !isPartyFull">
                  ต้องเลือกให้ครบ {{ maxAttackers }} คน (ขาด {{ remainingParty }} คน)
                </template>
                <template v-else>
                  {{ attackMode === 'seize' ? 'บุกยึด outpost' : 'เคลียร์ผู้ยึดครอง' }}
                </template>
              </span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </template>
        </footer>

        <!-- Item Details Modal -->
        <div
          v-if="selectedItem"
          class="fixed inset-0 z-60 flex items-center justify-center bg-gray-900 bg-opacity-80 transition-opacity duration-300 animate-modalFade"
        >
          <div
            class="bg-gray-800 rounded-md w-full max-w-md p-4 shadow-lg border border-gray-700 animate-scaleIn"
            style="max-height:70vh; overflow:auto;"
          >
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-semibold text-indigo-300">{{ selectedItem }}</h3>
              <button
                @click="selectedItem = null"
                class="text-gray-300 hover:text-indigo-300 group focus:outline-none p-2"
                aria-label="ปิดหน้าต่างรายละเอียดไอเทม"
              >
                <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>

            <div class="text-indigo-200 text-sm space-y-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="itemDetails[selectedItem]?.img"
                  :src="itemDetails[selectedItem].img"
                  :alt="selectedItem"
                  class="w-12 h-12 object-cover rounded-md border border-gray-700"
                  @error="handleImageError"
                />

                <div>
                  <div v-if="itemDetails[selectedItem]?.type">
                    <span class="font-medium">ประเภท:</span> {{ itemDetails[selectedItem]?.type }}
                  </div>
                  <div v-if="itemDetails[selectedItem]?.rarity">
                    <span class="font-medium">ความหายาก:</span> {{ itemDetails[selectedItem]?.rarity }}
                  </div>
                </div>
              </div>

              <div v-if="itemDetails[selectedItem]?.requiredLevel > 0">
                <span class="font-medium">เลเวลขั้นต่ำ:</span> {{ itemDetails[selectedItem]?.requiredLevel }}
              </div>

              <div v-if="itemDetails[selectedItem]?.weight > 0">
                <span class="font-medium">น้ำหนัก:</span> {{ itemDetails[selectedItem]?.weight }}
              </div>

              <div v-if="itemDetails[selectedItem]?.price > 0">
                <span class="font-medium">ราคา:</span> {{ itemDetails[selectedItem]?.price }}
              </div>

              <div v-if="itemDetails[selectedItem]?.description && itemDetails[selectedItem]?.description !== 'ไม่มี'">
                <span class="font-medium">คำอธิบาย:</span> {{ itemDetails[selectedItem]?.description }}
              </div>

              <div v-if="hasNonZeroStats(itemDetails[selectedItem]?.stats)">
                <span class="font-medium">ค่าสถานะ:</span>
                <ul class="list-disc ml-4 text-xs">
                  <li v-for="(val, key) in displayStats(itemDetails[selectedItem]?.stats)" :key="key">
                    {{ statLabel(key) }}: {{ val }}
                  </li>
                </ul>
              </div>

              <div v-if="itemDetails[selectedItem]?.type === 'รักษา' && itemDetails[selectedItem]?.healHpPercentage > 0">
                <span class="font-medium">HP ที่ได้รับ:</span> {{ itemDetails[selectedItem]?.healHpPercentage }}%
              </div>

              <div>
                <span class="font-medium">แยกส่วนได้:</span>
                {{ itemDetails[selectedItem]?.decomposable === true ? 'ใช่' : 'ไม่' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Villager Power Modal -->
        <VillagerPowerModal
          v-if="showPowerModal"
          :villager="selectedVillager"
          @close="showPowerModal = false"
        />
      </div>
    </div>
  </teleport>
</template>
<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import { projectFirestore, projectFunctions } from '@/firebase/config'
import getUser from '@/composables/getUser'
import { usePlayerStore } from '@/composables/usePlayerStore'
import VillagerPowerModal from '@/components/villager/VillagerPowerModal.vue'
import gameItemsData from '@/data/gameItems.json'
import gameVillagersData from '@/data/gameVillagers.json'

export default {
  name: 'OutpostModal',
  props: {
    outpostId: { type: String, required: true }
  },
  emits: ['close'],
  components: {
    VillagerPowerModal
  },
  setup (props, { emit }) {
    const { user } = getUser()

    const activeTab = ref('basic')
    const tabs = ref([
      { id: 'basic', name: 'ข้อมูลพื้นฐาน', icon: 'fas fa-info-circle' },
      { id: 'loot', name: 'สมบัติที่ดรอปได้', icon: 'fas fa-sack-dollar' },
      { id: 'defenders', name: 'NPC ป้องกัน', icon: 'fas fa-people-group' }
    ])

    const filteredTabs = computed(() => {
      // ห้ามซ่อน tab defenders
      // คนนอกเห็น tab ได้ แต่เห็นเฉพาะ NPC
      // เจ้าของ/admin เห็นชาวบ้านจริงจาก outpostPrivate.defenders
      return tabs.value
    })

    const outpost = ref(null)
    const outpostPrivate = ref(null)
    const privateAccessDenied = ref(false)
    const imgBroken = ref(false)

    let stop = null
    let stopPrivate = null
    let privateSubscribedId = null
    let prevBodyOverflow = ''
    let interval = null

    // ใช้กัน race condition:
    // load NPC รอบเก่าห้ามมาทับ load ชาวบ้านจริงรอบใหม่
    let defendersLoadSeq = 0

    const itemDetails = ref({})
    const villagerDetails = ref({})
    const defendersData = ref([])
    const isLoadingItem = ref({})
    const isLoadingVillager = ref({})
    const selectedItem = ref(null)

    const isSelectingVillagers = ref(false)
    const selectedVillagerIds = ref([])
    const playerVillagers = ref([])
    const templateCache = ref({})

    const attackStarting = ref(false)
    const attacking = ref(false)
    const attackMode = ref('seize')
    const isAnonymous = ref(false)

    const currentTime = ref(Date.now())
    const hasMaxOutposts = ref(false)

    const characterName = ref('NPC')
    const characterImageUrl = ref(null)

    const playerStore = usePlayerStore()
    const isAdmin = ref(false)
    watch(() => playerStore.userData, (data) => {
      isAdmin.value = data?.admin === true
    }, { immediate: true })

    const isPlainObject = (value) => {
      return value !== null && typeof value === 'object' && !Array.isArray(value)
    }

    const publicOwnerUid = computed(() => {
      return outpost.value?.ownerUid || null
    })

    const privateOwnerUid = computed(() => {
      return outpostPrivate.value?.ownerUid || null
    })

    const effectiveOwnerUid = computed(() => {
      return publicOwnerUid.value || privateOwnerUid.value || null
    })

    const isOwnOutpost = computed(() => {
      if (!user.value?.uid) return false

      return publicOwnerUid.value === user.value.uid ||
        privateOwnerUid.value === user.value.uid
    })

    const isNpcOwned = computed(() => {
      return !effectiveOwnerUid.value
    })

    const canSeeRealDefenders = computed(() => {
      return isAdmin.value || isOwnOutpost.value
    })

    const getPrivateDefendersMap = () => {
      if (isPlainObject(outpostPrivate.value?.defenders)) {
        return outpostPrivate.value.defenders
      }

      return {}
    }

    const getPublicPlayerDefendersMap = () => {
      if (isPlainObject(outpost.value?.defenders)) {
        return outpost.value.defenders
      }

      return {}
    }

    const getNpcDefenderNames = () => {
      if (Array.isArray(outpost.value?.defenders)) {
        return outpost.value.defenders
      }

      if (Array.isArray(outpost.value?.npcDefenders)) {
        return outpost.value.npcDefenders
      }

      if (Array.isArray(outpost.value?.defaultDefenders)) {
        return outpost.value.defaultDefenders
      }

      if (Array.isArray(outpost.value?.originalDefenders)) {
        return outpost.value.originalDefenders
      }

      return []
    }

    const normalizePlayerDefender = (id, rawDefender) => {
      const states = rawDefender?.states || {}

      // Schema V2:
      // defenders.{villagerId}.states เก็บ snapshot ครบแล้ว
      // ไม่ต้องเอา villagerId ไปตรวจใน camps.villagers
      const templateId = rawDefender?.templateId || states?.templateId || id
      const template = gameVillagersData?.[templateId] || {}

      return {
        id,
        name: template?.name || templateId,
        templateId,

        ...template,
        ...states,

        template,
        stats: template?.stats || rawDefender?.stats || {},
        upgradeStats: states?.upgradeStats || rawDefender?.upgradeStats || {},

        assignedAt: states?.assignedAt || rawDefender?.assignedAt || null,
        createdAt: states?.createdAt || rawDefender?.createdAt || null,
        lastUsedInAttackAt: states?.lastUsedInAttackAt || rawDefender?.lastUsedInAttackAt || null,

        isPlayerDefender: true,
        isNpcDefender: false
      }
    }

    const defendersTabName = computed(() => {
      if (!canSeeRealDefenders.value) {
        return 'NPC ป้องกัน'
      }

      const privateCount = Object.keys(getPrivateDefendersMap()).length
      const publicPlayerCount = Object.keys(getPublicPlayerDefendersMap()).length
      const legacyCount = (outpostPrivate.value?.defenderVillagerIds || []).length

      return (privateCount + publicPlayerCount + legacyCount) > 0
        ? 'ชาวบ้านป้องกัน'
        : 'NPC ป้องกัน'
    })

    const remainingOutpostMs = computed(() => {
      if (!outpost.value?.lastAttackedAt) return 0

      const last = outpost.value.lastAttackedAt.toDate().getTime()
      return Math.max(0, 60 * 60 * 1000 - (currentTime.value - last))
    })

    const remainingClearedMs = computed(() => {
      if (!outpost.value?.clearedUntil) return 0

      const until = outpost.value.clearedUntil.toDate().getTime()
      return Math.max(0, until - currentTime.value)
    })

    const isClearCooldown = computed(() => remainingClearedMs.value > 0)

    const maxAttackers = computed(() => outpost.value?.maxAttackerVillagers || 5)

    const treasureMessage = ref('')
    const isOpeningTreasure = ref(false)
    const confirmOpen = ref(false)
    const showHintOpen = ref(false)
    const countdownText = ref('')

    const treasureOpened = computed(() => outpost.value?.treasureOpened || false)

    const remainingTreasureMs = computed(() => {
      if (!outpost.value?.treasureAvailableAt) return 0

      const available = outpost.value.treasureAvailableAt.toDate().getTime()
      return Math.max(0, available - currentTime.value)
    })

    const isTreasureAvailable = computed(() => {
      return !treasureOpened.value && remainingTreasureMs.value <= 0
    })

    const updateCountdown = () => {
      const now = new Date(currentTime.value)
      const resetTime = new Date(now)

      resetTime.setHours(21, 0, 0, 0)

      if (now > resetTime) {
        resetTime.setDate(resetTime.getDate() + 1)
      }

      const diff = resetTime - now

      if (diff > 0) {
        const hours = Math.floor(diff / 3600000)
        const minutes = Math.floor((diff % 3600000) / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)

        countdownText.value = `รีเซ็ตใน ${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      } else {
        countdownText.value = 'รีเซ็ตแล้ว - สามารถเปิดได้'
      }
    }

    const openTreasureBox = async () => {
      confirmOpen.value = false
      isOpeningTreasure.value = true
      treasureMessage.value = ''

      try {
        const { data } = await projectFunctions.httpsCallable('gameActions')({
          action: 'outpostActions',
          subAction: 'openTreasureBox',
          outpostId: props.outpostId
        })

        if (data.success) {
          treasureMessage.value = data.message
        } else {
          treasureMessage.value = data.error || 'เกิดข้อผิดพลาด'
        }
      } catch (e) {
        treasureMessage.value = e.message || 'เกิดข้อผิดพลาดในการเปิดกล่อง'
      } finally {
        isOpeningTreasure.value = false
      }
    }

    const subscribe = (id) => {
      if (stop) stop()

      imgBroken.value = false
      outpost.value = null
      defendersData.value = []

      const docRef = projectFirestore.collection('outposts').doc(id)

      stop = docRef.onSnapshot(
        snap => {
          if (!snap.exists) {
            outpost.value = null
            defendersData.value = []
            return
          }

          outpost.value = snap.data()
        },
        err => {
          console.error('subscribe outpost error:', err)
        }
      )
    }

    const subscribePrivate = (id) => {
      if (!user.value?.uid) return

      if (privateSubscribedId === id && stopPrivate) return

      if (stopPrivate) stopPrivate()

      privateSubscribedId = id
      privateAccessDenied.value = false
      outpostPrivate.value = null

      const docRef = projectFirestore.collection('outpostPrivate').doc(id)

      stopPrivate = docRef.onSnapshot(
        snap => {
          privateAccessDenied.value = false

          if (!snap.exists) {
            outpostPrivate.value = null
            return
          }

          outpostPrivate.value = snap.data()
        },
        err => {
          console.error('subscribe outpostPrivate error:', err)

          privateAccessDenied.value = true
          outpostPrivate.value = null
        }
      )
    }

    const stopPrivateSubscription = () => {
      if (stopPrivate) {
        stopPrivate()
        stopPrivate = null
      }

      privateSubscribedId = null
      privateAccessDenied.value = false
      outpostPrivate.value = null
    }

    const emitClose = () => {
      emit('close')
    }

    const onKey = (e) => {
      if (e.key === 'Escape') emitClose()
    }

    const toPercent = (chance) => {
      const n = Number(chance) || 0
      return (n / 10000 * 100).toFixed(2)
    }

    const fetchItemDetails = (itemName) => {
      if (!itemName) return
      if (itemDetails.value[itemName] || isLoadingItem.value[itemName]) return

      isLoadingItem.value[itemName] = true

      try {
        itemDetails.value[itemName] = gameItemsData[itemName] || null
      } catch (e) {
        itemDetails.value[itemName] = null
      } finally {
        isLoadingItem.value[itemName] = false
      }
    }

    const fetchVillagerDetails = async (npcName) => {
      if (!npcName) return
      if (villagerDetails.value[npcName] || isLoadingVillager.value[npcName]) return

      isLoadingVillager.value[npcName] = true

      try {
        const doc = await projectFirestore.collection('gameVillagers').doc(npcName).get()
        villagerDetails.value[npcName] = doc.exists ? doc.data() : null
      } catch (e) {
        console.error('fetchVillagerDetails error:', e)
        villagerDetails.value[npcName] = null
      } finally {
        isLoadingVillager.value[npcName] = false
      }
    }

    const fetchTemplate = async (name) => {
      if (templateCache.value[name]) return templateCache.value[name]

      const t = gameVillagersData[name] || null
      templateCache.value[name] = t

      return t
    }

    const fetchCharacter = async () => {
      const ownerUid = effectiveOwnerUid.value

      if (ownerUid) {
        try {
          const charDoc = await projectFirestore.collection('characters').doc(ownerUid).get()

          if (charDoc.exists) {
            const charData = charDoc.data()
            characterName.value = charData.characterName || ownerUid
            characterImageUrl.value = charData.imageUrl || null
          } else {
            characterName.value = ownerUid
            characterImageUrl.value = null
          }
        } catch (e) {
          console.error('Fetch character error:', e)
          characterName.value = ownerUid
          characterImageUrl.value = null
        }
      } else {
        characterName.value = 'NPC'
        characterImageUrl.value = null
      }
    }

    const loadNpcDefenders = async (seq) => {
      const npcNames = getNpcDefenderNames()

      if (npcNames.length > 0) {
        await Promise.all(npcNames.map(fetchVillagerDetails))

        // ถ้ามี loadDefenders รอบใหม่เริ่มแล้ว ห้ามเอาข้อมูลเก่ามาทับ
        if (seq !== defendersLoadSeq) return

        defendersData.value = npcNames.map(npc => ({
          id: npc,
          name: npc,
          ...(villagerDetails.value[npc] || {}),
          template: null,
          upgradeStats: {},
          isPlayerDefender: false,
          isNpcDefender: true
        }))
      } else {
        if (seq !== defendersLoadSeq) return
        defendersData.value = []
      }

      if (seq !== defendersLoadSeq) return
      tabs.value[2].name = 'NPC ป้องกัน'
    }

    const loadDefenders = async (source = '') => {
      const seq = ++defendersLoadSeq

      defendersData.value = []

      const privateDefendersMap = getPrivateDefendersMap()
      const publicPlayerDefendersMap = getPublicPlayerDefendersMap()
      const legacyIds = outpostPrivate.value?.defenderVillagerIds || []

      const playerDefendersMap = Object.keys(privateDefendersMap).length > 0
        ? privateDefendersMap
        : publicPlayerDefendersMap

      const playerEntries = Object.entries(playerDefendersMap)

      console.log('[OutpostModal loadDefenders]', {
        source,
        seq,
        userUid: user.value?.uid || null,
        publicOwnerUid: publicOwnerUid.value,
        privateOwnerUid: privateOwnerUid.value,
        effectiveOwnerUid: effectiveOwnerUid.value,
        isOwnOutpost: isOwnOutpost.value,
        isAdmin: isAdmin.value,
        canSeeRealDefenders: canSeeRealDefenders.value,
        privateAccessDenied: privateAccessDenied.value,
        privateDefenderCount: Object.keys(privateDefendersMap).length,
        publicPlayerDefenderCount: Object.keys(publicPlayerDefendersMap).length,
        playerEntriesCount: playerEntries.length,
        legacyCount: legacyIds.length,
        npcCount: getNpcDefenderNames().length
      })

      // เจ้าของ/admin: ถ้า private ยังไม่โหลด และมีสิทธิ์อ่าน private
      // อย่าเพิ่ง fallback ไป NPC เพราะ NPC รอบเก่าอาจทับชาวบ้านจริง
      if (
        canSeeRealDefenders.value &&
        !outpostPrivate.value &&
        !privateAccessDenied.value &&
        user.value?.uid
      ) {
        tabs.value[2].name = 'ชาวบ้านป้องกัน'
        return
      }

      // เจ้าของ/admin: เห็นชาวบ้านจริงจาก outpostPrivate.defenders ก่อนเสมอ
      if (canSeeRealDefenders.value) {
        if (playerEntries.length > 0) {
          if (seq !== defendersLoadSeq) return

          defendersData.value = playerEntries.map(([id, rawDefender]) => {
            return normalizePlayerDefender(id, rawDefender)
          })

          tabs.value[2].name = 'ชาวบ้านป้องกัน'

          console.log('[OutpostModal defendersData set PLAYER]', {
            seq,
            count: defendersData.value.length,
            names: defendersData.value.map(d => d.name)
          })

          return
        }

        if (legacyIds.length > 0) {
          if (seq !== defendersLoadSeq) return

          defendersData.value = legacyIds.map(id => ({
            id,
            name: 'ชาวบ้านแบบเก่า (ต้อง Migrate)',
            isLegacy: true,
            isPlayerDefender: true,
            isNpcDefender: false,
            template: null,
            upgradeStats: {}
          }))

          tabs.value[2].name = 'ชาวบ้านป้องกัน'
          return
        }

        // ถ้า private โหลดแล้วและไม่มีชาวบ้านจริง ค่อย fallback ไป NPC
        await loadNpcDefenders(seq)
        return
      }

      // คนนอก: เห็นเฉพาะ NPC เท่านั้น
      await loadNpcDefenders(seq)
    }

    const showItemDetails = (itemName) => {
      fetchItemDetails(itemName)

      if (itemDetails.value[itemName]) {
        selectedItem.value = itemName
      }
    }

    const handleImageError = (e) => {
      e.target.src = 'https://via.placeholder.com/64?text=No+Img'
    }

    const hasNonZeroStats = (stats) => {
      return stats && Object.values(stats).some(v => Number(v) > 0)
    }

    const statOrder = [
      'stamina',
      'str',
      'int',
      'agi',
      'luck',
      'defense',
      'maxHp',
      'atk',
      'evasion',
      'critical'
    ]

    const statLabel = (k) => ({
      stamina: 'Stamina',
      str: 'Strength',
      int: 'Intelligence',
      agi: 'Agility',
      luck: 'Luck',
      defense: 'Defense',
      maxHp: 'Max HP',
      atk: 'Attack',
      evasion: 'Evasion',
      critical: 'Critical'
    }[k] || k)

    const displayStats = (stats) => {
      if (!stats) return {}

      const entries = Object.entries(stats).filter(([, v]) => Number(v) > 0)

      entries.sort((a, b) => {
        return statOrder.indexOf(a[0]) - statOrder.indexOf(b[0])
      })

      return Object.fromEntries(entries)
    }

    const getTotalStat = (v, key) => {
      const base = v.stats?.[key] || v.template?.stats?.[key] || 0
      const upgrade = v.upgradeStats?.[key] || 0

      return base + upgrade
    }

    const getDice = (v, key) => {
      return v[key] || v.template?.[key] || 0
    }

    const remainingMs = (v) => {
      if (!v.lastUsedInAttackAt) return 0

      const last = v.lastUsedInAttackAt.toDate().getTime()
      return Math.max(0, 20 * 60 * 60 * 1000 - (currentTime.value - last))
    }

    const formatRemaining = (ms) => {
      if (ms <= 0) return '00:00:00'

      const hours = Math.floor(ms / 3600000)
      const minutes = Math.floor((ms % 3600000) / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    const formatRemainingLong = (ms) => {
      if (ms <= 0) return '00:00:00'

      const days = Math.floor(ms / 86400000)
      const hours = Math.floor((ms % 86400000) / 3600000)
      const minutes = Math.floor((ms % 3600000) / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)

      if (days > 0) {
        return `${days} วัน ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      }

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    const isNameAlreadySelected = (name) => {
      return selectedVillagerIds.value.some(id => {
        const villager = playerVillagers.value.find(v => v.id === id)
        return villager && villager.name === name
      })
    }

    const isSelectable = (v) => {
      const selected = selectedVillagerIds.value.includes(v.id)

      if (selected) return true

      const nameSelected = isNameAlreadySelected(v.name)

      return remainingMs(v) <= 0 &&
        v.canAssign &&
        !nameSelected &&
        selectedVillagerIds.value.length < maxAttackers.value
    }

    const sortedPlayerVillagers = computed(() => {
      const selectable = playerVillagers.value.filter(v => isSelectable(v))
      const nonSelectable = playerVillagers.value.filter(v => !isSelectable(v))

      const sortFn = (a, b) => {
        const readyA = remainingMs(a) <= 0 ? 1 : 0
        const readyB = remainingMs(b) <= 0 ? 1 : 0

        if (readyA !== readyB) return readyB - readyA

        const levelA = a.level ?? a.template?.level ?? 0
        const levelB = b.level ?? b.template?.level ?? 0

        if (levelA !== levelB) return levelB - levelA

        const attackA = getDice(a, 'attackDice')
        const attackB = getDice(b, 'attackDice')

        return attackB - attackA
      }

      return [...selectable.sort(sortFn), ...nonSelectable.sort(sortFn)]
    })

    const sortedLootItems = computed(() => {
      return [...(outpost.value?.lootItems || [])].sort((a, b) => b.chance - a.chance)
    })

    const protectedOwnerUid = 'iSglrP9xyYTtjOUefKK3gD4O4wW2'
    const isProtectedOwner = computed(() => effectiveOwnerUid.value === protectedOwnerUid)
    const cannotClear = computed(() => isNpcOwned.value || isProtectedOwner.value)

    const attackDisabled = computed(() => {
      return outpost.value?.event ||
        isClearCooldown.value ||
        remainingOutpostMs.value > 0 ||
        attackStarting.value ||
        (hasMaxOutposts.value && cannotClear.value)
    })

    const disableReason = computed(() => {
      if (outpost.value?.event) return 'outpost นี้กำลังมีเหตุการณ์พิเศษเกิดขึ้น'
      if (isClearCooldown.value) return `อยู่ในช่วงฟื้นตัว สามารถยึดได้อีก ${formatRemainingLong(remainingClearedMs.value)}`
      if (remainingOutpostMs.value > 0) return `สามารถบุกยึดได้หลัง ${formatRemaining(remainingOutpostMs.value)}`
      if (attackStarting.value) return 'กำลังเตรียมการบุกยึด...'
      if (hasMaxOutposts.value && isProtectedOwner.value) return 'ไม่สามารถเคลียร์ outpost ของผู้เล่นรายนี้ได้'
      if (hasMaxOutposts.value && isNpcOwned.value) return 'ไม่สามารถเคลียร์ outpost ของ NPC ได้'

      return 'ไม่สามารถบุกยึดได้ในขณะนี้'
    })

    const startAttack = async () => {
      if (!user.value || isOwnOutpost.value || attackDisabled.value) return

      attackStarting.value = true
      isSelectingVillagers.value = true
      selectedVillagerIds.value = []
      playerVillagers.value = []
      attackMode.value = hasMaxOutposts.value ? 'clear' : 'seize'

      try {
        const campSnap = await projectFirestore.collection('camps').doc(user.value.uid).get()
        if (!campSnap.exists) return

        const villagersObj = campSnap.data().villagers || {}
        const outpostLocation = outpost.value?.location

        const mappedVillagers = Object.entries(villagersObj).map(([id, v]) => {
          const states = v.states || {}
          const templateId = v.templateId || states.templateId
          const template = gameVillagersData?.[templateId] || null
          const locs = template?.locations || []
          const canAssign = locs.length === 0 || locs.includes(outpostLocation)

          return {
            id,
            name: template?.name || templateId || id,
            templateId,
            ...states,
            template,
            upgradeStats: states?.upgradeStats || {},
            canAssign
          }
        })

        mappedVillagers.sort((a, b) => {
          const readyA = remainingMs(a) <= 0 ? 1 : 0
          const readyB = remainingMs(b) <= 0 ? 1 : 0

          if (readyA !== readyB) return readyB - readyA

          const levelA = a.level ?? a.template?.level ?? 0
          const levelB = b.level ?? b.template?.level ?? 0

          if (levelA !== levelB) return levelB - levelA

          const attackA = getDice(a, 'attackDice')
          const attackB = getDice(b, 'attackDice')

          return attackB - attackA
        })

        playerVillagers.value = mappedVillagers
      } catch (e) {
        console.error('Error fetching villagers for seize:', e)
      } finally {
        attackStarting.value = false
      }
    }

    const cancelSeize = () => {
      isSelectingVillagers.value = false
      selectedVillagerIds.value = []
      playerVillagers.value = []
      isAnonymous.value = false
    }

    const toggleSelect = (vid) => {
      const villager = playerVillagers.value.find(v => v.id === vid)

      if (!villager) return

      if (selectedVillagerIds.value.includes(vid)) {
        selectedVillagerIds.value = selectedVillagerIds.value.filter(id => id !== vid)
      } else if (isSelectable(villager)) {
        selectedVillagerIds.value.push(vid)
      }
    }

    const attackOutpost = async () => {
      attacking.value = true

      try {
        const { data } = await projectFunctions.httpsCallable('gameActions')({
          action: 'outpostActions',
          subAction: 'attackOutpost2',
          outpostId: props.outpostId,
          villagerIds: selectedVillagerIds.value,
          mode: attackMode.value,
          anonymous: attackMode.value === 'clear' ? isAnonymous.value : false
        })

        if (data.success) {
          console.log(data.message)
          cancelSeize()
          emitClose()
        } else {
          console.error(data.error)
        }
      } catch (e) {
        console.error('Error attacking outpost:', e)
      } finally {
        attacking.value = false
      }
    }

    const selectedVillager = ref(null)
    const showPowerModal = ref(false)

    const openPowerModal = (v) => {
      // คนนอกไม่ควรเห็นรายละเอียดชาวบ้านจริง
      if (v?.isPlayerDefender && !canSeeRealDefenders.value) {
        return
      }

      selectedVillager.value = v
      showPowerModal.value = true
    }

    const validDefenders = computed(() => {
      return Array.isArray(defendersData.value)
        ? defendersData.value.filter(d => d !== null)
        : []
    })

    const invalidDefenderIds = computed(() => {
      // Schema V2:
      // outpostPrivate.defenders เก็บ snapshot ของชาวบ้านไว้ครบแล้ว
      // ไม่ต้องตรวจ id กับ camps.villagers หรือ defendersData อีก
      return []
    })

    const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
      } catch (e) {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
    }

    const removeInvalidDefender = async () => {
      // Deprecated in defender schema V2
    }

    const removeAllInvalidDefenders = async () => {
      // Deprecated in defender schema V2
    }

    const migrateOutpostDefenders = async () => {
      if (!isAdmin.value) return
      if (!confirm('Migrate defenders ของ outpost นี้เป็น V2?')) return

      try {
        const { data } = await projectFunctions.httpsCallable('gameActions')({
          action: 'outpostActions',
          subAction: 'migrateOutpostDefenders',
          outpostId: props.outpostId
        })

        if (data.success) {
          alert(data.message)
          await loadDefenders('migrate')
        }
      } catch (e) {
        console.error('Error migrating outpost defenders:', e)
        alert('Migrate ไม่สำเร็จ: ' + (e.message || 'เกิดข้อผิดพลาด'))
      }
    }

    onMounted(() => {
      subscribe(props.outpostId)

      document.addEventListener('keydown', onKey)
      prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      updateCountdown()

      interval = setInterval(() => {
        currentTime.value = Date.now()
        updateCountdown()
      }, 1000)

      if (user.value?.uid) {
        projectFirestore.collection('outposts').where('ownerUid', '==', user.value.uid).get().then(snap => {
          hasMaxOutposts.value = snap.size >= 2
        }).catch(e => {
          console.error('Error checking max outposts:', e)
        })
      }
    })

    watch(() => props.outpostId, (id) => {
      if (id) {
        stopPrivateSubscription()
        subscribe(id)
      }
    })

    watch([outpost, user, isAdmin], async () => {
      if (!outpost.value) return

      if (outpost.value.lootItems?.length) {
        outpost.value.lootItems.forEach(loot => fetchItemDetails(loot.item))
      }

      // สำคัญ:
      // กรณี ownerUid อยู่ใน outpostPrivate เท่านั้น
      // ต้องลอง subscribe outpostPrivate ก่อน ไม่งั้นเจ้าของจะไม่เห็นชาวบ้านป้องกัน
      if (user.value?.uid) {
        const publicHasOtherOwner = publicOwnerUid.value && publicOwnerUid.value !== user.value.uid

        if (isAdmin.value || !publicHasOtherOwner || publicOwnerUid.value === user.value.uid) {
          subscribePrivate(props.outpostId)
        } else {
          stopPrivateSubscription()
        }
      } else {
        stopPrivateSubscription()
      }

      await loadDefenders('outpost/user/admin')
      await fetchCharacter()
    })

    watch(outpostPrivate, async () => {
      await loadDefenders('private')
      await fetchCharacter()
    })

    onBeforeUnmount(() => {
      if (stop) stop()
      if (stopPrivate) stopPrivate()

      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevBodyOverflow || ''

      if (interval) clearInterval(interval)
    })

    const mustPickMax = computed(() => true)
    const isPartyFull = computed(() => selectedVillagerIds.value.length === maxAttackers.value)
    const remainingParty = computed(() => Math.max(0, maxAttackers.value - selectedVillagerIds.value.length))

    return {
      mustPickMax,
      isPartyFull,
      remainingParty,
      cannotClear,

      activeTab,
      tabs,
      filteredTabs,

      outpost,
      outpostPrivate,
      imgBroken,
      emitClose,

      toPercent,
      itemDetails,
      villagerDetails,
      defendersData,
      isLoadingItem,
      isLoadingVillager,
      selectedItem,
      showItemDetails,
      handleImageError,

      hasNonZeroStats,
      displayStats,
      statLabel,
      getTotalStat,
      getDice,

      isSelectingVillagers,
      selectedVillagerIds,
      playerVillagers,
      startAttack,
      cancelSeize,
      toggleSelect,
      attackOutpost,

      defendersTabName,
      isOwnOutpost,
      remainingMs,
      formatRemaining,
      formatRemainingLong,
      remainingOutpostMs,
      remainingClearedMs,
      isClearCooldown,

      attackStarting,
      attacking,
      hasMaxOutposts,

      characterName,
      characterImageUrl,
      isAnonymous,

      openTreasureBox,
      isOpeningTreasure,
      confirmOpen,
      showHintOpen,
      treasureMessage,
      countdownText,
      remainingTreasureMs,
      treasureOpened,
      isTreasureAvailable,

      attackMode,
      isNpcOwned,
      maxAttackers,

      isNameAlreadySelected,
      sortedPlayerVillagers,
      isSelectable,
      sortedLootItems,

      attackDisabled,
      disableReason,

      selectedVillager,
      showPowerModal,
      openPowerModal,

      isAdmin,
      validDefenders,
      invalidDefenderIds,
      removeInvalidDefender,
      removeAllInvalidDefenders,
      migrateOutpostDefenders,
      copyToClipboard
    }
  }
}
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }
@keyframes scaleIn { from { transform: scale(0.97); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.25s ease-out; }
@keyframes tabSlide { from { width: 0; } to { width: 100%; } }
.animate-tabSlide { animation: tabSlide 0.2s ease-out; }
.text-xxs { font-size: 0.68rem; line-height: 1rem; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
@keyframes shake {
  0% { transform: translateX(0); }
  10% { transform: translateX(-5px) rotate(-5deg); }
  20% { transform: translateX(5px) rotate(5deg); }
  30% { transform: translateX(-5px) rotate(-5deg); }
  40% { transform: translateX(5px) rotate(5deg); }
  50% { transform: translateX(-5px) rotate(-5deg); }
  60% { transform: translateX(5px) rotate(5deg); }
  70% { transform: translateX(-5px) rotate(-5deg); }
  80% { transform: translateX(5px) rotate(5deg); }
  90% { transform: translateX(-5px) rotate(-5deg); }
  100% { transform: translateX(0); }
}
.animate-shake { animation: shake 2.5s ease-in-out infinite; }
</style>
